import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { 
  Plus, Search, FileText, Clock, CheckCircle, AlertTriangle, 
  ChevronRight, Settings, LogOut, Shield, Users, GitCompare, X, Trash2, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: FileText },
  in_progress: { label: 'In Progress', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Clock },
  completed: { label: 'Completed', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
};

const RISK_CONFIG = {
  Critical: { color: 'text-red-600 bg-red-50 border-red-200', priority: 0 },
  High: { color: 'text-orange-600 bg-orange-50 border-orange-200', priority: 1 },
  Medium: { color: 'text-amber-600 bg-amber-50 border-amber-200', priority: 2 },
  Low: { color: 'text-emerald-600 bg-emerald-50 border-emerald-200', priority: 3 },
};

const AssessmentDashboard = ({ onOpenAssessment, onNewAssessment, onOpenAdmin, isAdmin, onLogout, onCompare }) => {
  const { user, authFetch } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [compareMode, setCompareMode] = useState(false);
  const [compareSelection, setCompareSelection] = useState([]);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const fetchAssessments = useCallback(async () => {
    try {
      const res = await authFetch('/api/assessments/all');
      let data;
      try {
        const text = await res.text();
        data = JSON.parse(text);
      } catch {
        data = [];
      }
      if (res.ok && Array.isArray(data)) {
        setAssessments(data);
      }
    } catch (err) {
      console.error('Failed to fetch assessments:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  const handleDeleteAssessment = async (assessmentId, clientName) => {
    if (!window.confirm(`Delete "${clientName}"? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`/api/assessments/${assessmentId}`, { method: 'DELETE' });
      if (res.ok) {
        setAssessments(prev => prev.filter(a => a.id !== assessmentId));
        setCompareSelection(prev => prev.filter(id => id !== assessmentId));
        toast.success('Assessment deleted');
      } else {
        toast.error('Failed to delete assessment');
      }
    } catch {
      toast.error('Failed to delete assessment');
    }
  };

  const filtered = assessments.filter(a => {
    const matchesSearch = !searchQuery || 
      a.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.user_email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: assessments.length,
    draft: assessments.filter(a => a.status === 'draft').length,
    inProgress: assessments.filter(a => a.status === 'in_progress').length,
    completed: assessments.filter(a => a.status === 'completed').length,
    critical: assessments.filter(a => a.audit_risk === 'Critical').length,
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="assessment-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/defenciq-logo.png" alt="DefencIQ" className="h-8 w-8" />
            <div>
              <h1 className="text-lg font-bold text-slate-900 leading-tight">
                DefencIQ<span className="text-[10px] align-super font-medium text-slate-400">TM</span>
              </h1>
              <p className="text-xs text-slate-500 -mt-0.5">Discovery Workbench</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={onOpenAdmin} className="gap-2" data-testid="admin-console-btn">
                <Settings className="h-4 w-4" />
                Admin
              </Button>
            )}
            <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer" data-testid="dashboard-user-menu">
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-white text-sm font-medium">
                      {user?.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="hidden sm:block text-left">
                      <p className="text-sm font-medium text-slate-700 leading-tight">{user?.name}</p>
                      <p className="text-xs text-slate-400">{user?.role}</p>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-medium">{user?.name}</span>
                      <span className="text-xs text-slate-500">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowChangePassword(true)} data-testid="dashboard-change-password-menu-item">
                    <KeyRound className="mr-2 h-4 w-4" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('all')}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('draft')}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Drafts</p>
              <p className="text-2xl font-bold text-slate-500 mt-1">{stats.draft}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('in_progress')}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">In Progress</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{stats.inProgress}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('completed')}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Completed</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{stats.completed}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow border-red-100" onClick={() => setStatusFilter('all')}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-red-500 uppercase tracking-wider">Critical Risk</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{stats.critical}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search + New Assessment */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by client, assessor..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-assessments"
            />
          </div>
          <div className="flex gap-2">
            <div className="flex border rounded-lg overflow-hidden">
              {['all', 'draft', 'in_progress', 'completed'].map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${
                    statusFilter === s 
                      ? 'bg-slate-800 text-white' 
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`filter-${s}`}
                >
                  {s === 'all' ? 'All' : s === 'in_progress' ? 'Active' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <Button onClick={onNewAssessment} className="gap-2 bg-slate-800 hover:bg-slate-700" data-testid="new-assessment-btn">
              <Plus className="h-4 w-4" />
              New Assessment
            </Button>
            {assessments.length >= 2 && (
              <Button
                variant={compareMode ? "default" : "outline"}
                onClick={() => { setCompareMode(!compareMode); setCompareSelection([]); }}
                className="gap-2"
                data-testid="compare-toggle-btn"
              >
                <GitCompare className="h-4 w-4" />
                {compareMode ? 'Cancel' : 'Compare'}
              </Button>
            )}
          </div>
        </div>

        {/* Compare selection banner */}
        {compareMode && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 mb-6" data-testid="compare-banner">
            <GitCompare className="h-5 w-5 text-blue-600 shrink-0" />
            <p className="text-sm text-blue-700 flex-1">
              {compareSelection.length === 0 && 'Select 2 assessments to compare'}
              {compareSelection.length === 1 && 'Select 1 more assessment to compare'}
              {compareSelection.length === 2 && 'Ready to compare!'}
            </p>
            {compareSelection.length === 2 && (
              <Button
                size="sm"
                onClick={() => onCompare(compareSelection)}
                className="gap-1 bg-blue-600 hover:bg-blue-700"
                data-testid="compare-now-btn"
              >
                Compare Now <ChevronRight className="h-3 w-3" />
              </Button>
            )}
            {compareSelection.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setCompareSelection([])} className="text-blue-600">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}

        {/* Assessment list */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto"></div>
            <p className="text-slate-400 mt-4 text-sm">Loading assessments...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">
              {assessments.length === 0 ? 'No assessments yet' : 'No matching assessments'}
            </h3>
            <p className="text-sm text-slate-400 mb-6">
              {assessments.length === 0 
                ? 'Create your first security assessment to get started.' 
                : 'Try adjusting your search or filter.'}
            </p>
            {assessments.length === 0 && (
              <Button onClick={onNewAssessment} className="gap-2" data-testid="empty-new-assessment-btn">
                <Plus className="h-4 w-4" />
                Create Assessment
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="assessment-list">
            {filtered.map(assessment => {
              const statusConf = STATUS_CONFIG[assessment.status] || STATUS_CONFIG.draft;
              const StatusIcon = statusConf.icon;
              const riskConf = assessment.audit_risk ? RISK_CONFIG[assessment.audit_risk] : null;
              const isSelected = compareSelection.includes(assessment.id);

              const handleCardClick = () => {
                if (compareMode) {
                  if (isSelected) {
                    setCompareSelection(prev => prev.filter(id => id !== assessment.id));
                  } else if (compareSelection.length < 2) {
                    setCompareSelection(prev => [...prev, assessment.id]);
                  }
                } else {
                  onOpenAssessment(assessment.id);
                }
              };

              return (
                <Card 
                  key={assessment.id}
                  className={`group hover:shadow-md transition-all cursor-pointer border-l-4 ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50/30' : ''}`}
                  style={{ borderLeftColor: assessment.audit_risk === 'Critical' ? '#dc2626' : assessment.audit_risk === 'High' ? '#ea580c' : assessment.status === 'completed' ? '#059669' : '#e2e8f0' }}
                  onClick={handleCardClick}
                  data-testid={`assessment-card-${assessment.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      {compareMode && (
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                          isSelected ? 'bg-blue-500 border-blue-500 text-white' : 'border-slate-300'
                        }`}>
                          {isSelected && <CheckCircle className="h-4 w-4" />}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1.5">
                          <h3 className="text-base font-semibold text-slate-900 truncate">
                            {assessment.client_name}
                          </h3>
                          <Badge variant="outline" className={`text-xs shrink-0 ${statusConf.color}`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConf.label}
                          </Badge>
                          {riskConf && (
                            <Badge variant="outline" className={`text-xs shrink-0 ${riskConf.color}`}>
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              {assessment.audit_risk} Risk
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {assessment.user_name}
                          </span>
                          <span>Created {formatDate(assessment.created_at)}</span>
                          <span>Updated {timeAgo(assessment.updated_at)}</span>
                          {assessment.overall_score !== null && assessment.overall_score !== undefined && (
                            <span className="font-medium text-slate-700">
                              Score: {assessment.overall_score}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="opacity-0 group-hover:opacity-100 transition-opacity gap-1"
                          onClick={(e) => { e.stopPropagation(); onOpenAssessment(assessment.id); }}
                          data-testid={`resume-btn-${assessment.id}`}
                        >
                          {assessment.status === 'completed' ? 'View' : 'Resume'}
                          <ChevronRight className="h-3 w-3" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={(e) => { e.stopPropagation(); handleDeleteAssessment(assessment.id, assessment.client_name); }}
                          data-testid={`delete-btn-${assessment.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Footer info */}
        <div className="mt-8 text-center text-xs text-slate-400">
          <p>Essential Eight ML2 & DISP Readiness Assessment</p>
        </div>
      </main>
      <ChangePasswordDialog open={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </div>
  );
};

export default AssessmentDashboard;
